/**
 * HR's company events (punch list S25, S26; Jeremy, 1 Nov 2026).
 *
 * A BLACKOUT stops PTO, no-travel days and field work on its days, for everyone; INFORMATION ONLY
 * shows on the team calendar and changes nothing. Bookings already inside a new blackout are left
 * as they are. To change one, remove it and add it again. Every add and remove is in the admin log.
 *
 * The words on this panel are Jeremy's, exactly; add none without asking him. A face, no rules:
 * the API enforces HR-only, the dates and the kinds.
 */
import { COMPANY_EVENT_KIND_LABEL, COMPANY_EVENT_KINDS, mdy, type CompanyEventKind, type CompanyEventView } from "@scheduler/shared";
import { useEffect, useState } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { useRefreshOnReturn } from "../refreshOnReturn";

/** "11/16/2026": the day as every date reads. */
const day = (d: string) => mdy(d);

export function CompanyEvents() {
  const api = useSchedulerApi();
  const [events, setEvents] = useState<CompanyEventView[] | null>(null);
  const [name, setName] = useState("");
  const [firstDay, setFirstDay] = useState("");
  const [lastDay, setLastDay] = useState("");
  const [kind, setKind] = useState<CompanyEventKind | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    api.companyEvents().then(setEvents, (e: unknown) => setError(message(e)));
  }, [api, back]);

  async function run(action: () => Promise<CompanyEventView[]>, after?: () => void) {
    setBusy(true);
    setError(null);
    try {
      setEvents(await action());
      after?.();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  const ready = name.trim() !== "" && firstDay !== "" && lastDay !== "" && lastDay >= firstDay && kind !== "";

  return (
    <div className="card card-pad">
      <h3>Company events</h3>

      {error ? <p className="error" role="alert">{error}</p> : null}

      {!events ? null : events.length === 0 ? (
        <p className="muted">No company events.</p>
      ) : (
        <table className="data" style={{ marginBottom: 16 }}>
          <thead>
            <tr><th>Name</th><th>First day</th><th>Last day</th><th>Kind</th><th /></tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td>{e.name}</td>
                <td>{day(e.firstDay)}</td>
                <td>{day(e.lastDay)}</td>
                <td>{COMPANY_EVENT_KIND_LABEL[e.kind]}</td>
                <td>
                  <button type="button" className="ghost" disabled={busy}
                    onClick={() => void run(() => api.companyEventRemove(e.id))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <label className="field">
        <span className="label">Name</span>
        <input type="text" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <div className="row2">
        <label className="field">
          <span className="label">First day</span>
          <input type="date" value={firstDay} onChange={(e) => { setFirstDay(e.target.value); if (!lastDay) setLastDay(e.target.value); }} />
        </label>
        <label className="field">
          <span className="label">Last day</span>
          <input type="date" value={lastDay} min={firstDay || undefined} onChange={(e) => setLastDay(e.target.value)} />
        </label>
      </div>
      <label className="field">
        <span className="label">Kind</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as CompanyEventKind | "")}>
          <option value="" />
          {COMPANY_EVENT_KINDS.map((k) => <option key={k} value={k}>{COMPANY_EVENT_KIND_LABEL[k]}</option>)}
        </select>
      </label>
      <button type="button" className="primary" disabled={!ready || busy}
        onClick={() => void run(
          () => api.companyEventAdd({ name: name.trim(), firstDay, lastDay, kind: kind as CompanyEventKind }),
          () => { setName(""); setFirstDay(""); setLastDay(""); setKind(""); },
        )}>
        Add event
      </button>
    </div>
  );
}

function message(e: unknown): string {
  return e instanceof ApiError ? e.message : String(e);
}
